import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import { toast } from 'react-toastify';
import PageHeader from '../common/PageHeader';
import ActionConfirmModal from '../common/ActionConfirmModal';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import apiBase from '../../apiConfig';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';

const initialForm = {
  login: '',
  password: '',
  full_name: '',
  user_type: 'doctor',
  role_group_id: '',
  filial_id: '',
  starttime: '00:00',
  endtime: '23:59',
  is_active: 1
};

const createInitialForm = userType => ({
  ...initialForm,
  user_type: userType || initialForm.user_type
});

const Staff = ({ title, description, listTitle, createLabel, filterUserType, lockUserType }) => {
  const [staff, setStaff] = useState([]);
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [roles, setRoles] = useState([]);
  const [filials, setFilials] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(createInitialForm(filterUserType));
  const [deleteTarget, setDeleteTarget] = useState(null);
  const filteredStaff = filterUserType ? staff.filter(user => user.user_type === filterUserType) : staff;

  const fetchData = async () => {
    const [staffRes, rolesRes, filialsRes] = await Promise.all([
      axios.get(`${apiBase}/staff.php`),
      axios.get(`${apiBase}/reference.php`, { params: { type: 'roles' } }),
      axios.get(`${apiBase}/reference.php`, { params: { type: 'filials' } })
    ]);

    if (staffRes.data.success) setStaff(staffRes.data.staff);
    if (rolesRes.data.success) setRoles(rolesRes.data.items);
    if (filialsRes.data.success) setFilials(filialsRes.data.items);
  };

  useEffect(() => {
    fetchData().catch(console.error);
  }, []);

  const columns = [
    { dataField: 'full_name', text: 'F.I.O', sort: true, classes: 'align-middle font-weight-bold' },
    { dataField: 'login', text: 'Login', sort: true, classes: 'align-middle' },
    { dataField: 'user_type', text: 'Tip', sort: true, classes: 'align-middle text-uppercase' },
    { dataField: 'role_name', text: 'Rol', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'filial_name', text: 'Filial', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'is_active',
      text: 'Holat',
      sort: true,
      classes: 'align-middle',
      formatter: value => <Badge color={value ? 'soft-success' : 'soft-secondary'}>{value ? 'Faol' : 'Nofaol'}</Badge>
    },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '6.5rem' },
      classes: 'align-middle',
      formatter: (data, user) => (
        <div className="med-actions-row">
          <Button color="link" className="p-0 med-action-btn" onClick={() => openEdit(user)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            color="link"
            className="p-0 text-danger med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget({ id: user.id, name: user.full_name })}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  const openCreate = () => {
    setEditingId(null);
    setForm(createInitialForm(filterUserType));
    setModalOpen(true);
  };

  const openEdit = user => {
    setEditingId(user.id);
    setForm({
      login: user.login,
      password: '',
      full_name: user.full_name,
      user_type: lockUserType && filterUserType ? filterUserType : user.user_type,
      role_group_id: user.role_group_id,
      filial_id: user.filial_id,
      starttime: user.starttime || '00:00',
      endtime: user.endtime || '23:59',
      is_active: user.is_active ? 1 : 0
    });
    setModalOpen(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      if (editingId) {
        const res = await axios.put(`${apiBase}/staff.php`, { ...form, id: editingId });
        if (res.data.success) toast.success(res.data.message);
      } else {
        const res = await axios.post(`${apiBase}/staff.php`, form);
        if (res.data.success) toast.success(res.data.message);
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Xatolik yuz berdi');
    }
  };

  const handleDeactivate = async () => {
    if (!deleteTarget) return;
    try {
      const res = await axios.delete(`${apiBase}/staff.php`, { data: { id: deleteTarget.id } });
      if (res.data.success) {
        toast.success(res.data.message);
        fetchData();
        setDeleteTarget(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Xatolik yuz berdi');
    }
  };

  return (
    <>
      <PageHeader title={title} description={description} className="mb-3" />
      <Card className="border-0 shadow-sm">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">{listTitle}</h5>
            <div className="d-flex align-items-center">
              <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size mr-3" />
              <Button color="outline-primary" className="med-outline-add-btn" onClick={openCreate}>
                <FontAwesomeIcon icon="plus" className="mr-2" />
                {createLabel}
              </Button>
            </div>
          </div>
          <MedDataTable
            data={filteredStaff}
            columns={columns}
            pageSize={pageSize}
            paginationResetKey={`${filterUserType || 'all'}-${filteredStaff.length}`}
            noDataIndication={<div className="text-center py-4 text-500">Xodimlar topilmadi</div>}
          />
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} centered>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          {editingId ? 'Xodimni tahrirlash' : 'Yangi xodim'}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>F.I.O</Label>
              <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} />
            </FormGroup>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Login</Label>
                  <Input value={form.login} onChange={e => setForm({ ...form, login: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Parol</Label>
                  <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Tip</Label>
                  {lockUserType && filterUserType ? (
                    <Input value={filterUserType} disabled />
                  ) : (
                    <Input type="select" value={form.user_type} onChange={e => setForm({ ...form, user_type: e.target.value })}>
                      <option value="admin">admin</option>
                      <option value="registration">registration</option>
                      <option value="cash">cash</option>
                      <option value="doctor">doctor</option>
                      <option value="labaratory">labaratory</option>
                      <option value="oued">oued</option>
                      <option value="xray">xray</option>
                    </Input>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Rol</Label>
                  <Input type="select" value={form.role_group_id} onChange={e => setForm({ ...form, role_group_id: e.target.value })}>
                    <option value="">Tanlang</option>
                    {roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Filial</Label>
                  <Input type="select" value={form.filial_id} onChange={e => setForm({ ...form, filial_id: e.target.value })}>
                    <option value="">Tanlang</option>
                    {filials.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Boshlanish</Label>
                  <Input type="time" value={form.starttime} onChange={e => setForm({ ...form, starttime: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Tugash</Label>
                  <Input type="time" value={form.endtime} onChange={e => setForm({ ...form, endtime: e.target.value })} />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              <FontAwesomeIcon icon="times" className="mr-2" />
              Bekor qilish
            </Button>
            <Button color="primary" type="submit">
              <FontAwesomeIcon icon="check-circle" className="mr-2" />
              Saqlash
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <ActionConfirmModal
        isOpen={Boolean(deleteTarget)}
        toggle={() => setDeleteTarget(null)}
        title="Xodimni nofaol qilish"
        message={deleteTarget ? `${deleteTarget.name} foydalanuvchisini nofaol holatga o'tkazmoqchimisiz?` : ''}
        confirmLabel="Nofaol qilish"
        onConfirm={handleDeactivate}
      />
    </>
  );
};

Staff.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  listTitle: PropTypes.string,
  createLabel: PropTypes.string,
  filterUserType: PropTypes.string,
  lockUserType: PropTypes.bool
};

Staff.defaultProps = {
  title: 'Xodimlar',
  description: "Foydalanuvchilar va rollarni boshqarish.",
  listTitle: "Xodimlar ro'yxati",
  createLabel: 'Yangi xodim',
  filterUserType: '',
  lockUserType: false
};

export default Staff;
